import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { AuthService } from '@/services/authService';
import { AuthError } from '@supabase/supabase-js';
import { UserService } from '@/services/userService';
import { supabase } from '@/integrations/supabase/client';
import { AuthResult, SignUpCredentials } from '@/contexts/auth/types';
import { sanitizeEmail, sanitizeUsername, validateEmail, validatePassword, validateUsername, isRateLimited, logSecurityEvent } from '@/utils/security';
import { logger } from '@/utils/logger';

interface SignUpOptions {
  username?: string;
  redirectTo?: string;
  [key: string]: any;
}

export const useSignUp = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const signUp = async (credentials: SignUpCredentials): Promise<AuthResult> => {
    const { email, password, username } = credentials;
    setIsLoading(true);
    setError(null);
    
    try {
      // Input validation and sanitization
      const sanitizedEmail = sanitizeEmail(email);
      const sanitizedUsername = username ? sanitizeUsername(username) : '';
      
      // Validate email
      if (!validateEmail(sanitizedEmail)) {
        throw new Error('Please enter a valid email address');
      }
      
      // Validate password
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.isValid) {
        throw new Error(passwordValidation.errors.join(' '));
      }
      
      // Validate username if provided
      if (sanitizedUsername) {
        const usernameValidation = validateUsername(sanitizedUsername);
        if (!usernameValidation.isValid) {
          throw new Error(usernameValidation.errors[0]);
        }
      }
      
      // Rate limiting check (max 3 signup attempts per hour)
      if (isRateLimited(`signup_${sanitizedEmail}`, 3, 60 * 60 * 1000)) {
        logSecurityEvent('signup_rate_limit_exceeded', { email: sanitizedEmail });
        throw new Error('Too many signup attempts. Please try again later.');
      }
      
      logger.debug("Starting signup process with:", { email: sanitizedEmail, username: sanitizedUsername });
      
      // Check if display name is already taken
      if (sanitizedUsername) {
        const { data: existingUsers, error: checkError } = await supabase
          .from('profiles')
          .select('id')
          .eq('display_name', sanitizedUsername);
          
        if (checkError) {
          logger.error("Error checking display name uniqueness:", checkError);
        } else if (existingUsers && existingUsers.length > 0) {
          logSecurityEvent('signup_username_taken', { email: sanitizedEmail, username: sanitizedUsername });
          toast({
            title: "Display name already taken",
            description: "Please choose a different display name.",
            variant: "destructive",
          });
          setIsLoading(false);
          setError(new Error("Display name already taken"));
          return { error: { message: "Display name already taken" } };
        }
      }
      
      // Sign up the user in Supabase Auth with sanitized input
      const options: SignUpOptions = {
        username: sanitizedUsername,
        redirectTo: window.location.origin + '/account'
      };
      
      const { data, error } = await AuthService.signUp(sanitizedEmail, password, options);
      
      if (error) {
        logSecurityEvent('signup_failed', { 
          email: sanitizedEmail, 
          username: sanitizedUsername,
          error: error.message 
        });
        throw error;
      }
      
      logSecurityEvent('signup_success', { 
        email: sanitizedEmail, 
        username: sanitizedUsername,
        userId: data.user?.id 
      });
      
      logger.debug("Auth signup result:", data.user?.id);
      
      // If sign up is successful, ensure profile is created
      if (data.user) {
        // Check if the user already has a session
        const { data: sessionData } = await supabase.auth.getSession();
        
        if (sessionData?.session) {
          try {
            // Attempt to create or fetch profile
            const profile = await UserService.fetchProfile(data.user.id);
            
            if (profile) {
              logger.debug("Profile created or fetched successfully:", profile.id);

              toast({
                title: "Account created",
                description: "Your account has been created successfully.",
              });
            }
          } catch (profileErr) {
            logger.error("Error creating or fetching profile:", profileErr);
            
            toast({
              title: "Account created",
              description: "Your account has been created. Profile will be set up on first login.",
            });
          }
        } else {
          logger.debug("No session available for profile creation, will be created on first login");
          
          // Still show success message
          toast({
            title: "Account created",
            description: "Your account has been created successfully. Please verify your email if required.",
          });
        }
      }
      
      return { user: data.user || undefined };
    } catch (error) {
      logger.error('Error signing up:', error);
      
      const errorMessage = error instanceof AuthError || error instanceof Error
        ? error.message
        : 'An unknown error occurred during sign up';
      
      setError(error instanceof Error ? error : new Error(errorMessage));
      
      toast({
        title: "Registration failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      return { error: { message: errorMessage } };
    } finally {
      setIsLoading(false);
    }
  };

  return { signUp, isLoading, error };
};
